import { Mail, Phone } from "lucide-react";
import { Reveal, SectionHead, GoldBtn, PageHero } from "@/components/Shared";
import EnquiryForm from "@/components/EnquiryForm";
import { PARTNERSHIP_TYPES, CONTACTS, EVENT } from "@/data/content";

export default function Partner() {
    return (
        <div data-testid="partner-page">
            <PageHero
                testid="partner-hero"
                eyebrow="Partnerships"
                title={<>Partner With <span className="gold-text">MahaBazaar</span></>}
                sub="Build the bazaar with us — strategic, industry, knowledge, technology, experience and media partnerships at national scale."
            >
                <GoldBtn to="#partner-enquiry" testid="partner-cta-btn">Become a Partner</GoldBtn>
            </PageHero>

            <section className="py-24 md:py-32">
                <div className="container-x">
                    <SectionHead eyebrow="Partnership Architecture" title={<>Six Ways to <span className="gold-text">Build Together</span></>} />
                    <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {PARTNERSHIP_TYPES.map((p, i) => {
                            const contact = CONTACTS.find((c) => c.email.startsWith(p.contact));
                            return (
                                <Reveal key={p.title} delay={(i % 3) * 80}>
                                    <div className="card-luxe tilt p-8 h-full flex flex-col" data-testid={`partnership-card-${i}`}>
                                        <div className="card-glow" />
                                        <div className="w-12 h-14 arch-top border border-[var(--line)] flex items-center justify-center">
                                            <p.icon size={20} strokeWidth={1.2} className="text-[var(--gold)]" />
                                        </div>
                                        <h3 className="font-display text-2xl mt-6 text-[var(--ivory)]">{p.title}</h3>
                                        <p className="mt-3 text-sm text-muted-luxe leading-relaxed flex-1">{p.desc}</p>
                                        {contact && (
                                            <a href={`mailto:${contact.email}`} data-testid={`partnership-email-${i}`}
                                                className="mt-6 inline-flex items-center gap-2 text-[0.62rem] tracking-[0.2em] uppercase text-[var(--champagne)] hover:text-[var(--gold)] transition-colors">
                                                <Mail size={12} /> {contact.email}
                                            </a>
                                        )}
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="py-24 md:py-32 bg-[#1C060C]/60">
                <div className="container-x">
                    <SectionHead eyebrow="The Partnership Team" title={<>Talk to the People <span className="gold-text">Building the Bazaar</span></>} />
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {CONTACTS.map((c, i) => (
                            <Reveal key={c.email} delay={(i % 3) * 70}>
                                <div className="card-luxe p-7 h-full" data-testid={`contact-card-${i}`}>
                                    <div className="card-glow" />
                                    <div className="w-10 h-10 arch-top border border-[var(--line)] flex items-center justify-center font-display gold-text text-lg">
                                        {c.name.charAt(0)}
                                    </div>
                                    <h3 className="font-display text-xl mt-5 text-[var(--ivory)]">{c.name}</h3>
                                    <p className="text-[0.62rem] tracking-[0.24em] uppercase text-[var(--gold)] mt-1.5">{c.role}</p>
                                    <p className="text-sm text-muted-luxe mt-3">{c.scope}</p>
                                    <a href={`mailto:${c.email}`} data-testid={`contact-email-${i}`}
                                        className="mt-5 inline-flex items-center gap-2 text-xs text-[var(--champagne)] hover:text-[var(--gold)] transition-colors break-all">
                                        <Mail size={12} className="shrink-0" /> {c.email}
                                    </a>
                                </div>
                            </Reveal>
                        ))}
                        <Reveal delay={210}>
                            <div className="card-luxe p-7 h-full flex flex-col justify-center" style={{ background: "linear-gradient(150deg, rgba(74,14,24,0.5), rgba(20,4,9,0.94))" }}>
                                <p className="text-[0.62rem] tracking-[0.3em] uppercase text-[var(--gold)]">Direct Line</p>
                                <a href={`tel:${EVENT.phone.replace(/\s/g, "")}`} data-testid="partner-phone"
                                    className="mt-4 flex items-center gap-3 font-display text-2xl text-[var(--ivory)] hover:text-[var(--champagne)] transition-colors">
                                    <Phone size={18} className="text-[var(--gold)]" /> {EVENT.phone}
                                </a>
                                <p className="mt-4 text-xs text-muted-luxe leading-relaxed">
                                    For partnerships, exhibition and general enquiries.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="py-24 md:py-32" id="partner-enquiry">
                <div className="container-x max-w-3xl">
                    <Reveal>
                        <EnquiryForm defaultCategory="Partnership Enquiry" heading="Become a Partner" sub="Tell us how your organisation wants to build the bazaar — the right team lead will respond." testidPrefix="partner-enquiry" />
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
